import { Group, Stack, Text, Textarea } from "@mantine/core";
import { useParams } from "@tanstack/react-router";
import { Archive, Scale } from "lucide-react";
import { useState } from "react";

import type { Voice } from "../../../api/generated/model";
import { AudioTtsQuote, AudioTtsRequest } from "../../../api/generated/zod";
import { ErrorAlert } from "../../../platform/ErrorAlert";
import {
  ProductListPopover,
  type ListedProduct,
} from "../../../platform/media/ProductListPopover";
import { ScriptActions, ScriptBusy } from "../../../platform/scripts/ScriptActions";
import { ScriptOverlay } from "../../../platform/scripts/ScriptOverlay";
import { useScriptJobs } from "../../../platform/scripts/use-script-jobs";
import { VoicePanel } from "../../../platform/voices/VoicePanel";
import { VoiceTile } from "../../../platform/voices/VoiceTile";
import { Choice } from "../../../ui/Choice";
import { GlassPanel } from "../../../ui/GlassPanel";
import { ModelPicker } from "../../../ui/ModelPicker";
import { SourceTool } from "../../../ui/SourceTool";
import { UsageNotes } from "../../../ui/UsageNotes";
import classes from "./composer.module.css";
import { defaultOf, type SoundFormProps } from "./SoundEffectsForm";
import { SoundErrors, StartRow } from "./StartRow";
import { stepsOf } from "./steps";
import { ROUTE, useSoundJob } from "./use-sound-job";

/** Live's words (`text-to-speech/constants.ts`, capture 02). */
const PLACEHOLDER =
  "Write your script... Use [emotion] to add emotions like [laughs], [sights], [excited]";
const MODEL_TIP = "Select the model for speech generation.";
/** Live's brief placeholder in Text-to-speech's "Generate Script" (capture L03). */
const BRIEF = "Describe what your voice should say...";
const STABILITY_TIP =
  "Controls how consistently the voice follows its original characteristics. Higher values produce more stable speech, while lower values allow greater expressiveness.";

/**
 * Live's Text-to-speech composer (#235, captures 02 and 03): the Voice Settings slot, which
 * opens live's voice picker beside the panel (#236, `platform/voices`), the "Script" with live's
 * two script tools (`platform/scripts`, live's `audio_generation` tool), the script and its
 * counter, then the model, the stability, the usage notes, the count, the price and the start.
 * Live prices and starts speech once it has a voice and a script.
 */
export function SpeechForm({ models, voiceDesign, above, onStarted, inline }: SoundFormProps) {
  const { teamId, brandId } = useParams({ from: ROUTE });
  const [voice, setVoice] = useState<Voice | null>(null);
  const [voicesOpen, setVoicesOpen] = useState(false);
  const [modelId, setModelId] = useState(defaultOf(models).id);
  const model = models.find((each) => each.id === modelId) ?? defaultOf(models);
  const block = model.speech!;
  const [prompt, setPrompt] = useState("");
  const [stability, setStability] = useState(block.stability.default);
  // Another model starts at its own block's stability, as live's form resets it.
  const choose = (id: string) => {
    const next = models.find((each) => each.id === id)?.speech;
    if (next === undefined || next === null) return;
    setModelId(id);
    setStability(next.stability.default);
  };
  const [collapsed, setCollapsed] = useState(false);
  // Live's "Generate Script" overlay, and its product, which lives and dies with it.
  const [scriptOpen, setScriptOpen] = useState(false);
  const [product, setProduct] = useState<ListedProduct | null>(null);
  const [productsOpen, setProductsOpen] = useState(false);
  // A script or an enhancement replaces the script, as live's `updateScript`.
  const scripts = useScriptJobs({ teamId, brandId, tool: "audio_generation", onScript: setPrompt });
  // Speech is priced by its script's characters, so the quote carries it (G7).
  const quote = AudioTtsQuote.safeParse({
    type: "audio.tts",
    model: model.id,
    prompt,
    stability,
  });
  const written = prompt.trim();
  const request =
    voice === null || written === ""
      ? undefined
      : AudioTtsRequest.safeParse({
          type: "audio.tts",
          model: model.id,
          prompt: written,
          stability,
          voice_id: voice.id,
        });
  // Live's estimate waits for a voice too: the bolt alone until both are there.
  const job = useSoundJob(
    quote.success ? quote.data : undefined,
    request?.success === true ? request.data : undefined,
    written !== "" && voice !== null,
    onStarted,
  );
  // Live's tools wait while the start or either job runs (`isActionDisabled`); the smiley also
  // waits for a script, live's shortest here being one character.
  const toolsFree = !job.start.isPending && !scripts.processing;
  const productTool = (
    <ProductListPopover
      teamId={teamId}
      brandId={brandId}
      chosenId={product?.productId ?? null}
      opened={productsOpen}
      onClose={() => setProductsOpen(false)}
      onPick={setProduct}
      position="top-start"
      anchor={(wrap) => (
        <SourceTool
          label="Products"
          icon={<Archive size={20} strokeWidth={1.5} />}
          view={product}
          removeLabel="Remove the product"
          onRemove={() => setProduct(null)}
          onClick={() => setProductsOpen((open) => !open)}
          open={productsOpen}
          disabled={scripts.generating}
          target={wrap}
        />
      )}
    />
  );
  return (
    <GlassPanel
      inline={inline}
      collapsed={collapsed}
      onCollapsedChange={(next) => {
        setCollapsed(next);
        // Live's chevron closes the voice panel too.
        setVoicesOpen(false);
      }}
      above={above}
      summary={<span>Text-to-speech</span>}
      cover={
        scriptOpen ? (
          <ScriptOverlay
            placeholder={BRIEF}
            footerLeft={productTool}
            pending={scripts.generating}
            error={scripts.generateError}
            onCancel={() => setScriptOpen(false)}
            onGenerate={(brief) =>
              scripts.generate(brief, product?.productId ?? null, () => setScriptOpen(false))
            }
          />
        ) : undefined
      }
      header={
        <VoiceTile
          voice={voice}
          onOpen={() => setVoicesOpen(true)}
          onRemove={() => setVoice(null)}
        />
      }
      aside={
        voicesOpen ? (
          <VoicePanel
            teamId={teamId}
            brandId={brandId}
            chosen={voice}
            onChoose={setVoice}
            onClose={() => setVoicesOpen(false)}
            designModel={voiceDesign}
          />
        ) : undefined
      }
      footer={
        <Group justify="space-between" wrap="nowrap" gap={12}>
          <Group gap={12} wrap="nowrap">
            <ModelPicker
              models={models}
              value={model.id}
              onChange={choose}
              bolts={false}
              tip={MODEL_TIP}
            />
            <Choice
              setting="Stability"
              icon={<Scale size={20} strokeWidth={1.5} />}
              chevron={false}
              width={80}
              tip={STABILITY_TIP}
              value={String(stability)}
              options={stepsOf(block.stability).map((each) => ({
                value: String(each),
                label: String(each),
              }))}
              onChange={(next) => setStability(Number(next))}
            />
            <UsageNotes notes={block.notes} />
          </Group>
          <StartRow job={job} />
        </Group>
      }
    >
      <Stack gap={4}>
        <div className={classes.script}>
          <Text className={classes.scriptLabel}>Script</Text>
          <ScriptActions
            canGenerate={toolsFree}
            canEnhance={toolsFree && prompt.trim() !== ""}
            enhancing={scripts.enhancing}
            // Live's Text-to-speech marks no failed script.
            failed={false}
            onGenerate={() => {
              // Each opening starts with no product, as live's overlay keeps its own.
              setProduct(null);
              setScriptOpen(true);
            }}
            onEnhance={() => scripts.enhance(prompt)}
          />
        </div>
        <ScriptBusy busy={scripts.processing}>
          <Textarea
            variant="unstyled"
            aria-label="Script"
            placeholder={PLACEHOLDER}
            autosize
            minRows={2}
            maxRows={6}
            maxLength={model.prompt_max_chars}
            disabled={scripts.processing}
            value={prompt}
            onChange={(event) => setPrompt(event.currentTarget.value)}
          />
        </ScriptBusy>
        {scripts.enhanceError === null ? null : <ErrorAlert error={scripts.enhanceError} />}
        <Text size="xs" c="var(--cai-color-text-muted)" ta="right">
          {prompt.length} / {model.prompt_max_chars}
        </Text>
        <SoundErrors job={job} />
      </Stack>
    </GlassPanel>
  );
}
